import { useCallback, useEffect, useRef } from "react";

import type { ChatOutput, OutputQuery, ReportingChatSSEEvent, TableColumn } from "./types";

import { useReportingChatStore } from "./store";

import { getHeaders, getUrl } from "../../../../api/services/defs/internal-api/internal-base-request";

import { extractSSEMessages, parseSSEMessage } from "./utils/parseSse";

const STREAM_TIMEOUT_MS = 120_000;

// The stream contract rejects more than 50 messages, and the agent only ever reads the last few
// turns (REPORTING_CHAT_RECENT_TURNS), so sending the whole store both wasted payload and hard-
// failed every request once a conversation passed ~25 exchanges. Any bound comfortably above the
// agent's window and below the contract's cap sends an identical set of turns to the model.
const MAX_HISTORY_MESSAGES = 20;

type UseReportingChatStreamReturn = {
	sendQuery: (query: string) => Promise<void>;
	abort: () => void;
	isStreaming: boolean;
};

// Reshape a reporting_chat_output frame into the typed payload the store attaches to a message.
// `query` (table/chart only) and `suggestions` (all kinds) are carried through unchanged so the
// save-as-artifact flow and suggestion chips can read them off the attached output.
const toOutput = (kind: ChatOutput["kind"], payload: Record<string, unknown>): ChatOutput => {
	const suggestions = payload.suggestions as string[] | undefined;
	if (kind === "answer") {
		return {
			kind,
			answer: String(payload.answer ?? ""),
			underlyingQuery: String(payload.underlyingQuery ?? ""),
			suggestions,
		};
	}
	if (kind === "table") {
		return {
			kind,
			title: String(payload.title ?? ""),
			rows: (payload.rows as Record<string, unknown>[]) ?? [],
			columns: (payload.columns as TableColumn[]) ?? [],
			rowCount: Number(payload.rowCount ?? 0),
			truncated: Boolean(payload.truncated),
			query: payload.query as OutputQuery | undefined,
			suggestions,
		};
	}
	return {
		kind: "chart",
		chartType: (payload.chartType as "bar" | "line" | "pie") ?? "bar",
		title: String(payload.title ?? ""),
		labels: (payload.labels as string[]) ?? [],
		categoryLabel: String(payload.categoryLabel ?? ""),
		valueLabel: String(payload.valueLabel ?? ""),
		datasets: (payload.datasets as { label: string; data: (number | null)[] }[]) ?? [],
		truncated: Boolean(payload.truncated),
		query: payload.query as OutputQuery | undefined,
		suggestions,
	};
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const MAX_FILTER_VALUE_LENGTH = 40;

const stringifyFilterValue = (value: unknown): string => {
	const str = JSON.stringify(value) ?? String(value);
	return str.length > MAX_FILTER_VALUE_LENGTH ? `${str.slice(0, MAX_FILTER_VALUE_LENGTH)}…` : str;
};

// params.summarizeBy entries — see ArtifactReportSummarizeBy / SummarizeByInputSchema.
const describeSummarizeBy = (entries: unknown): string | undefined => {
	if (!Array.isArray(entries) || entries.length === 0) return undefined;
	const parts = entries.filter(isRecord).map((entry) => {
		const columnId = String(entry.columnId ?? "");
		return entry.selection !== undefined && entry.selection !== null
			? `${columnId} (${String(entry.selection)})`
			: columnId;
	});
	return parts.length > 0 ? `grouped by ${parts.join(", ")}` : undefined;
};

// params.aggregationOperations entries — see ArtifactReportAggregationOperation.
const describeAggregations = (entries: unknown): string | undefined => {
	if (!Array.isArray(entries) || entries.length === 0) return undefined;
	const parts = entries
		.filter(isRecord)
		.map((entry) => `${String(entry.operation ?? "")}(${String(entry.columnId ?? "")})`);
	return parts.length > 0 ? parts.join(", ") : undefined;
};

// params.filters — an array of OR'd groups, AND'd together (see ArtifactReportFilterEntry).
// `comparison` defaults to equality when omitted, matching translateReportFilters.
const describeFilters = (filterGroups: unknown): string | undefined => {
	if (!Array.isArray(filterGroups) || filterGroups.length === 0) return undefined;
	const groups = filterGroups
		.filter((group): group is unknown[] => Array.isArray(group) && group.length > 0)
		.map((group) =>
			group
				.filter(isRecord)
				.map(
					(entry) =>
						`${String(entry.columnId ?? "")} ${String(entry.comparison ?? "eq")} ${stringifyFilterValue(entry.value)}`
				)
				.join(" or ")
		);
	return groups.length > 0 ? `filters: ${groups.join(" and ")}` : undefined;
};

// Accepts either bare registry-id strings or column-shaped objects (ArtifactReportColumn), or an
// already-extracted string[] (table outputs' own `columns`).
const describeColumnIds = (columns: unknown): string | undefined => {
	if (!Array.isArray(columns) || columns.length === 0) return undefined;
	const ids = columns
		.map((column) => (typeof column === "string" ? column : isRecord(column) ? String(column.id ?? "") : ""))
		.filter((id) => id.length > 0);
	return ids.length > 0 ? `columns: ${ids.join(", ")}` : undefined;
};

// One compact history line per output, built from the REAL executed-query fields (reportType,
// summarizeBy, aggregationOperations, filters, column ids) so a follow-up like "show that as a
// table" still has them once the originating turn falls out of the backend's 6-turn window — the
// note-taker can't recover this, it's contractually barred from tool results and assistant prose.
// Answers carry no structured query, only the free-text `underlyingQuery` used by the ask-again
// suggestion flow, so that's included verbatim (untruncated — it IS the information to preserve).
export const describeOutputForHistory = (output: ChatOutput): string => {
	if (output.kind === "answer") {
		return output.underlyingQuery ? `[Displayed answer: ${output.underlyingQuery}]` : "[Displayed answer]";
	}

	const params = (output.query?.params ?? {}) as Record<string, unknown>;
	const columnIds = output.kind === "table" ? output.columns.map((column) => column.id) : params.columns;

	const sections = [
		output.query?.reportType,
		describeSummarizeBy(params.summarizeBy),
		describeAggregations(params.aggregationOperations),
		describeFilters(params.filters),
		describeColumnIds(columnIds),
	].filter((section): section is string => typeof section === "string" && section.length > 0);

	return sections.length > 0 ? `[Displayed ${output.kind}: ${sections.join(" | ")}]` : `[Displayed ${output.kind}]`;
};

export const useReportingChatStream = (): UseReportingChatStreamReturn => {
	const abortControllerRef = useRef<AbortController | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

	useEffect(() => {
		return () => {
			abortControllerRef.current?.abort();
			abortControllerRef.current = null;
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
			readerRef.current?.cancel().catch(() => {});
			readerRef.current = null;

			// This cleanup just nulled the ref isOwner() checks against, making it the last owner — the
			// aborted call's later catch will find isOwner() false, so this must hand status back itself.
			const { status, setStatus, setStreamStage, setStreamMessage } = useReportingChatStore.getState();
			if (status === "thinking" || status === "streaming") {
				setStatus("idle");
				setStreamStage(null);
				setStreamMessage(null);
			}
		};
	}, []);

	const status = useReportingChatStore((state) => state.status);

	const clearTimeoutRef = useCallback(() => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		timeoutRef.current = null;
	}, []);

	const abort = useCallback(() => {
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
		clearTimeoutRef();
		readerRef.current?.cancel().catch(() => {});
		readerRef.current = null;
		const { setStatus, setStreamStage, setStreamMessage } = useReportingChatStore.getState();
		setStatus("idle");
		setStreamStage(null);
		setStreamMessage(null);
	}, [clearTimeoutRef]);

	const sendQuery = useCallback(
		async (query: string): Promise<void> => {
			const store = useReportingChatStore.getState();
			const {
				addUserMessage,
				startAssistantMessage,
				appendToAssistantMessage,
				attachOutput,
				completeAssistantMessage,
				setNotes,
				setStatus,
				setStreamStage,
				setStreamMessage,
				setError,
			} = store;

			// Captured per call, not read back off the refs: this call installs its controller/timer
			// synchronously while the aborted previous call's finally is still queued as a microtask,
			// so that finally would otherwise clear the refs this call just claimed — leaving it with
			// no timeout and no way to be aborted.
			abortControllerRef.current?.abort();
			const controller = new AbortController();
			abortControllerRef.current = controller;

			// Invariant: only the live (owning) request may mutate global stream state — status, the
			// stage/progress labels, notes, and the error banner. A stale call can still be running
			// (draining SSE frames it already pulled off the buffer, or firing an already-scheduled
			// timeout below) after a newer sendQuery has taken over the shared controller, so every
			// global write in this call is gated on isOwner(). Message-id-scoped writes —
			// appendToAssistantMessage, attachOutput, completeAssistantMessage — stay unconditional;
			// they belong to this call's own message and must keep running regardless of ownership.
			const isOwner = () => abortControllerRef.current === controller;

			clearTimeoutRef();
			const timer = setTimeout(() => {
				controller.abort();
				if (isOwner()) useReportingChatStore.getState().setError("Request timed out. Please try again.");
			}, STREAM_TIMEOUT_MS);
			timeoutRef.current = timer;
			let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

			// History = prior turns; the new query is sent separately. Completed turns with attached
			// outputs get a describeOutputForHistory() line per output appended to their content — the
			// executed reportType/filters/groupings/columnIds must survive past the backend's 6-turn
			// window for a later "show that as a table" to still work, and notes can't cover it (the
			// note-taker never sees tool results or assistant prose). Excludes any still-streaming
			// message — under an overlapping sendQuery (see the isOwner() invariant above) the store can
			// still hold a superseded call's in-flight assistant message, and it isn't a completed turn yet.
			const history = store.messages
				.filter((message) => !message.isStreaming)
				.slice(-MAX_HISTORY_MESSAGES)
				.map((message) => ({
					role: message.role,
					content: message.outputs?.length
						? [message.content, ...message.outputs.map(describeOutputForHistory)].filter(Boolean).join("\n")
						: message.content,
				}));
			const notes = store.notes;

			addUserMessage(query);
			setError(null);
			setStreamStage(null);
			setStreamMessage(null);
			setStatus("thinking");
			const assistantMessageId = startAssistantMessage();

			try {
				const url = getUrl("/v1/reporting-chat/stream");
				const headers = getHeaders({ body: JSON.stringify({}) });

				const response = await fetch(url, {
					method: "POST",
					headers: { ...headers, "Content-Type": "application/json" } as Record<string, string>,
					body: JSON.stringify({ query, messages: history, ...(notes ? { notes } : {}) }),
					signal: controller.signal,
				});

				if (!response.ok) {
					const errorData = await response.json().catch(() => ({}));
					throw new Error(errorData.message || `Request failed: ${response.statusText}`);
				}
				if (!response.body) throw new Error("Response body is null");

				const activeReader = response.body.getReader();
				reader = activeReader;
				readerRef.current = activeReader;
				const decoder = new TextDecoder();
				let buffer = "";

				let streamDone = false;
				while (!streamDone) {
					const { done, value } = await activeReader.read();
					if (done) {
						streamDone = true;
						continue;
					}
					buffer += decoder.decode(value, { stream: true });

					const { messages: sseMessages, remainingBuffer } = extractSSEMessages(buffer);
					buffer = remainingBuffer;

					for (const message of sseMessages) {
						const parsed = parseSSEMessage(message);
						if (!parsed) continue;
						const event = { ...parsed.data, type: parsed.eventType } as ReportingChatSSEEvent;

						// See the isOwner() invariant above — a stale call can still be looping over frames
						// it already pulled off the buffer, so every case below that touches global state
						// (as opposed to this call's own message) checks ownership first.
						switch (event.type) {
							case "status":
								if (isOwner()) {
									setStreamStage(event.stage ?? null);
									setStreamMessage(event.message ?? null);
								}
								break;
							case "chunk":
								if (isOwner() && useReportingChatStore.getState().status === "thinking") {
									setStatus("streaming");
								}
								appendToAssistantMessage(assistantMessageId, event.text);
								break;
							case "reporting_chat_notes":
								if (isOwner()) setNotes(event.notes);
								break;
							case "reporting_chat_output":
								attachOutput(assistantMessageId, toOutput(event.kind, event.payload));
								break;
							// The finally clears this call's timer and cancels its reader regardless of
							// ownership; completeAssistantMessage (message-only, see store.ts) always runs too.
							case "done":
								completeAssistantMessage(assistantMessageId);
								if (isOwner()) setStatus("idle");
								return;
							case "error":
								// Guarding setError itself (not just a status write after it) matters here: a
								// dead call must not paint its error text over a live conversation either.
								if (isOwner()) setError(event.message);
								completeAssistantMessage(assistantMessageId, true);
								return;
						}
					}
				}

				// Stream ended without a done event — mark incomplete so the cursor stops.
				completeAssistantMessage(assistantMessageId, true);
				if (isOwner()) setStatus("idle");
			} catch (err) {
				completeAssistantMessage(assistantMessageId, true);
				if (err instanceof Error && err.name === "AbortError") {
					if (isOwner()) setStatus("idle");
					return;
				}
				if (isOwner()) setError(err instanceof Error ? err.message : "An unexpected error occurred");
			} finally {
				// Always tear down this call's own resources, but only release a shared ref while it
				// still points at this call — a newer sendQuery may already own it.
				clearTimeout(timer);
				reader?.cancel().catch(() => {});
				if (timeoutRef.current === timer) timeoutRef.current = null;
				if (abortControllerRef.current === controller) abortControllerRef.current = null;
				if (readerRef.current === reader) readerRef.current = null;
			}
		},
		[clearTimeoutRef]
	);

	return { sendQuery, abort, isStreaming: status === "thinking" || status === "streaming" };
};
